import type { BlockageCreateInput } from "@/hooks/api/blockage";
import type { ComunidadCalendarEvent } from "@/hooks/api/comunidad";

/** Fin de día usado por el bloqueo de "todo el día" de la Agenda (ver useBlockSchedule). */
const END_OF_DAY = "23:59";

export const pad = (n: number) => String(n).padStart(2, "0");
const utcDate = (d: Date) => `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
const utcTime = (d: Date) => `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`;

/** Motivo con el que queda el bloqueo en la Agenda (también sirve para no duplicarlo). */
export function formacionBlockReason(ev: Pick<ComunidadCalendarEvent, "title" | "edition">): string {
  return ev.edition ? `Formación: ${ev.title} (${ev.edition}° edición)` : `Formación: ${ev.title}`;
}

/**
 * Parte la ventana del evento en los bloqueos a crear. Casi siempre es uno solo; si el evento
 * cruza la medianoche UTC (ej. 21:00–23:30 en Buenos Aires = 00:00–02:30 UTC del día siguiente)
 * se parte en dos, porque `createBlockage` trabaja con un solo día por llamada.
 * Todo en UTC: es lo que espera `createBlockage`.
 */
export function splitWindowIntoBlockages(startIso: string, durationMinutes: number): BlockageCreateInput[] {
  const start = new Date(startIso);
  const end = new Date(start.getTime() + durationMinutes * 60_000);
  const startDay = utcDate(start);
  const endDay = utcDate(end);

  if (startDay === endDay) {
    return [{ date: startDay, start_time: utcTime(start), end_time: utcTime(end) }];
  }
  // Termina justo a las 00:00 UTC del día siguiente: todo cabe en el día de inicio.
  if (utcTime(end) === "00:00") {
    return [{ date: startDay, start_time: utcTime(start), end_time: END_OF_DAY }];
  }
  return [
    { date: startDay, start_time: utcTime(start), end_time: END_OF_DAY },
    { date: endDay, start_time: "00:00", end_time: utcTime(end) },
  ];
}

/** ¿Se pisan [aStart, aEnd) y [bStart, bEnd)? Tocarse en el borde no cuenta. */
export function rangesOverlap(aStart: number, aEnd: number, bStart: number, bEnd: number): boolean {
  return aStart < bEnd && bStart < aEnd;
}
